import type { CSSProperties } from "react";

import { ColumnChart, type ColumnSeries } from "@/components/charts";
import SectionCard from "@buildpy/ui/section-card";
import { EMPTY_MARK, formatNumber } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { type DesignerRate, FUNNEL_CONTRACT_NOTE, type FunnelStep, type LeadSpan, type MonthBucket } from "@buildpy/core/modules/insight/stage-funnel";

// 막대가 너무 짧아 건수가 안 들어가지 않게 — 첫 단계 대비 폭의 바닥(%).
const MIN_BAR_PCT = 12;

// 막대 폭(%)이 이보다 좁으면 건수를 막대 오른쪽 바깥에 전경색으로 쓴다 — 폰에서 흰 글자가 막대 밖으로 새지 않게.
export const LABEL_INSIDE_MIN_PCT = 28;

// 폰(375px)에서 막대가 차지할 수 있는 폭 · 라벨 한 글자(text-sm 굵게) · 좌우 여백 — 28% ≈ 49px 에 맞춘 어림값.
const PHONE_TRACK_PX = 175;
const LABEL_CHAR_PX = 9;
const LABEL_PAD_PX = 12;

// 라벨(「1,234건」)이 막대 안에 들어가려면 필요한 폭(%) — 글자가 길수록 올라가고 바닥은 LABEL_INSIDE_MIN_PCT.
export const labelInsideMinPct = (label: string): number =>
  Math.max(LABEL_INSIDE_MIN_PCT, Math.ceil(((label.length * LABEL_CHAR_PX + LABEL_PAD_PX) / PHONE_TRACK_PX) * 100));

export const labelFitsInside = (widthPct: number, label: string): boolean => widthPct >= labelInsideMinPct(label);

// 월별 막대 위 숫자는 달이 이보다 많으면 숨긴다 — 폰에서 겹친다(값은 sr-only 표에 있다).
export const MONTH_VALUES_MAX = 6;

export const funnelBarPct = (share: number | null): number => Math.max(Math.round((share ?? 0) * 100), MIN_BAR_PCT);

const pct = (rate: number | null): string => (rate === null ? "" : `${Math.round(rate * 100)}%`);

const MONTH_SERIES: ColumnSeries[] = [
  { key: "inquiries", label: "문의", tone: "primary-soft" },
  { key: "won", label: "계약", tone: "primary" },
];

export const FunnelPanel = ({ steps, contractNote = false }: { steps: readonly FunnelStep[]; contractNote?: boolean }) => (
  <SectionCard description="오른쪽 % = 앞 단계에서 넘어온 비율" title="단계별로 남은 고객">
    <ol className="flex flex-col gap-y-2" data-testid="insight-funnel">
      {steps.map((step) => {
        const width = funnelBarPct(step.share);
        const label = `${formatNumber(step.count)}건`;
        const inside = labelFitsInside(width, label);
        return (
          <li key={step.key} className="flex items-center gap-x-3" data-step={step.key}>
            <span className="w-16 shrink-0 text-sm font-medium sm:w-20">{step.label}</span>
            <div className="flex min-w-0 flex-1 justify-center">
              <div className="relative flex h-9 w-(--w) items-center justify-center rounded-md bg-primary text-sm font-semibold tabular-nums" style={{ "--w": `${width}%` } as CSSProperties}>
                <span
                  className={inside ? "text-primary-foreground" : "absolute left-full ml-1.5 whitespace-nowrap text-foreground"}
                  data-label={inside ? "inside" : "outside"}>
                  {label}
                </span>
              </div>
            </div>
            <span
              className={cn("w-12 shrink-0 text-right text-sm font-semibold tabular-nums", step.low ? "text-warning" : "text-muted-foreground")}
              data-low={step.low ? "true" : undefined}>
              {pct(step.conversion)}
              {step.low && <span className="sr-only"> 낮음</span>}
            </span>
          </li>
        );
      })}
    </ol>
    {contractNote && (
      <p className="mt-3 text-xs text-muted-foreground" data-testid="insight-funnel-note">
        {FUNNEL_CONTRACT_NOTE}
      </p>
    )}
  </SectionCard>
);

export const MonthlyPanel = ({ months }: { months: readonly MonthBucket[] }) => (
  <SectionCard
    actions={
      <div aria-hidden className="flex gap-x-3.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-x-1.5">
          <span className="size-2.5 rounded-sm bg-primary/40" />
          문의
        </span>
        <span className="flex items-center gap-x-1.5">
          <span className="size-2.5 rounded-sm bg-primary" />
          계약
        </span>
      </div>
    }
    description="그 달 문의 중 계약까지 간 고객"
    title="월별 문의 · 계약">
    <div aria-hidden data-testid="insight-monthly-chart">
      <ColumnChart
        data={months.map((m) => ({ label: m.label, values: { inquiries: m.inquiries, won: m.won } }))}
        series={MONTH_SERIES}
        showValues={months.length <= MONTH_VALUES_MAX}
      />
    </div>
    <table className="sr-only" data-testid="insight-monthly">
      <thead>
        <tr>
          <th scope="col">달</th>
          <th scope="col">문의</th>
          <th scope="col">계약</th>
        </tr>
      </thead>
      <tbody>
        {months.map((m) => (
          <tr key={m.key}>
            <th scope="row">{m.label}</th>
            <td>{m.inquiries}</td>
            <td>{m.won}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </SectionCard>
);

export const LeadPanel = ({ spans }: { spans: readonly LeadSpan[] }) => {
  const longest = Math.max(0, ...spans.map((s) => s.avg_days ?? 0));
  return (
    <SectionCard description="평균 · 가장 긴 구간이 병목" title="단계마다 걸린 날">
      <ul className="flex flex-col gap-y-3" data-testid="insight-lead">
        {spans.map((span) => (
          <li key={span.key} className="flex flex-col gap-y-1.5" data-bottleneck={span.bottleneck ? "true" : undefined}>
            <div className="flex items-baseline justify-between gap-x-2 text-sm">
              <span className="min-w-0">
                {span.label}
                {span.bottleneck && <span className="ml-1.5 text-xs font-medium text-warning">병목</span>}
              </span>
              <span className="shrink-0 font-semibold tabular-nums">{span.avg_days === null ? EMPTY_MARK : `${span.avg_days}일`}</span>
            </div>
            <div aria-hidden className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={cn("h-2 w-(--w) rounded-full", span.bottleneck ? "bg-warning" : "bg-muted-foreground")}
                style={{ "--w": `${longest > 0 ? Math.round(((span.avg_days ?? 0) / longest) * 100) : 0}%` } as CSSProperties}
              />
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

export const DesignerPanel = ({ rows }: { rows: readonly DesignerRate[] }) => (
  <SectionCard description="견적을 보낸 고객 중 계약까지 간 비율" title="담당 디자이너별 계약률">
    {rows.length === 0 ? (
      <p className="text-sm text-muted-foreground">이 기간에 견적을 보낸 고객이 없습니다.</p>
    ) : (
      <ul className="flex flex-col gap-y-3" data-testid="insight-designers">
        {rows.map((row) => (
          <li key={row.assignee_id ?? "none"} className="flex items-center gap-x-3">
            <span className="w-20 shrink-0 truncate text-sm">{row.name}</span>
            <div aria-hidden className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-2 w-(--w) rounded-full bg-primary" style={{ "--w": `${Math.round(row.rate * 100)}%` } as CSSProperties} />
            </div>
            <span className="w-24 shrink-0 text-right text-sm tabular-nums">
              <strong className="font-semibold">{pct(row.rate)}</strong>{" "}
              <span className="text-muted-foreground">
                {row.won}/{row.quoted}
              </span>
            </span>
          </li>
        ))}
      </ul>
    )}
  </SectionCard>
);
